import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { BodyCopy } from '@xing-com/typography';

import { MetaData } from '../../components/meta-data';
import { PageError } from '../../components/page-error';
import { SkeletonLoggedOutIndustriesPage } from '../../components/skeleton';
import { useCompaniesExperiments } from '../../hooks/use-companies-experiments/use-companies-experiments';
import { useCompaniesIndustries } from '../../hooks/use-companies-industries';
import { useTrackPage } from '../../hooks/use-track-page';
import {
  getIndustryRoute,
  getIndustrySearchPageRoute,
} from '../../routes/routes';
import { formatAmount } from '../../utils/format-amount';
import { pagesTrackingName } from '../../utils/tracking';
import { getIndustryName } from '../../utils/url';
import * as Styled from './industries.styles';
import { NewIndustries } from './new-industries';

export const INDUSTRIES_LIMIT = 100;
type IndustriesContent = {
  link: string;
  className: string;
  total: string;
  localizationValue: string;
  id: string;
};
const IndustryContent = ({
  localizationValue,
  total,
}: {
  localizationValue: string;
  total: string;
}) => (
  <>
    <BodyCopy fontWeight="bold" size="small" noMargin>
      {localizationValue}
    </BodyCopy>
    <BodyCopy size="small" noMargin>
      <FormattedMessage id={'CP_TOTAL_COMPANIES'} values={{ total }} />
    </BodyCopy>
  </>
);

const LoggedInIndustryContent = ({
  link,
  localizationValue,
  total,
  id,
  className,
}: IndustriesContent) => (
  <a
    key={id}
    className={className}
    href={link}
    data-testid={`loggedin-industry-${id}`}
  >
    <IndustryContent localizationValue={localizationValue} total={total} />
  </a>
);

const LoggedOutIndustryContent = ({
  link,
  localizationValue,
  total,
  id,
  className,
}: IndustriesContent) => (
  <Link
    key={id}
    className={className}
    to={link}
    data-testid={`loggedout-industry-${id}`}
  >
    <IndustryContent localizationValue={localizationValue} total={total} />
  </Link>
);

const CurrentIndustries: React.FC = () => {
  const intl = useIntl();
  const { isLoggedIn } = useLoginState();
  const { data, loading, error } = useCompaniesIndustries({
    limit: INDUSTRIES_LIMIT,
  });

  useTrackPage({ pageName: pagesTrackingName.industries });

  if (loading) return <SkeletonLoggedOutIndustriesPage />;
  if (error) return <PageError />;

  return (
    <>
      <MetaData
        index
        title={intl.formatMessage({ id: 'CP_100_META_TITLE_INDUSTRIES' })}
        description={intl.formatMessage({
          id: 'CP_INDUSTRIES_DESCRIPTION',
        })}
      />
      <Styled.IndustriTitle size="xlarge" as="h1">
        <FormattedMessage id="CP_100_META_TITLE_INDUSTRIES" />
      </Styled.IndustriTitle>
      <BodyCopy size="medium">
        <FormattedMessage id="CP_100_DIRECTORY_INTRO" />
      </BodyCopy>
      <BodyCopy fontWeight="bold" size="medium">
        <FormattedMessage id="CP_INDUSTRIES_DESCRIPTION" />
      </BodyCopy>
      <Styled.Container>
        {data?.companiesIndustries?.collection.map((item) => {
          const id = item?.industry?.id as string;
          const localizationValue = item?.industry?.localizationValue as string;
          const total = item?.total as number;

          const link = isLoggedIn
            ? getIndustrySearchPageRoute({ id })
            : getIndustryRoute({ id, name: getIndustryName({ intl, id }) });

          const IndustryComponent = isLoggedIn
            ? LoggedInIndustryContent
            : LoggedOutIndustryContent;

          return (
            <IndustryComponent
              key={id}
              id={id}
              className="industry"
              link={link}
              total={formatAmount({ value: total, intl })}
              localizationValue={localizationValue}
            />
          );
        })}
      </Styled.Container>
    </>
  );
};

export const Industries: React.FC = () => {
  const { isLoggedIn } = useLoginState();
  const { isNewLoggedOutExperimentEnabled } = useCompaniesExperiments();
  const IndustriesPage =
    isNewLoggedOutExperimentEnabled && !isLoggedIn
      ? NewIndustries
      : CurrentIndustries;
  return <IndustriesPage />;
};
